import React, { useEffect, useContext, useState } from "react";
import { Button, Grid } from "@material-ui/core";
import { KeyboardTab } from "@material-ui/icons";
import { useMutation, useQuery } from "@apollo/client";
import _ from "lodash";

import logger from "../../utils/logger";
import {
  APP_INSIGHTS_EVENTS,
  EMPTY_GRAPHS_VALUES,
  MAX_INPUT_VALUE,
  RADIO_SWITCHER_VALUES,
  STOCKPILES_TABLE_FEATURES,
} from "../../constants";
import { SEND_STOCKPILES_TABLE_DATA } from "../../mutations/table-data.gql";

import TimeRangePicker from "../time-range-picker/time-range-picker.component";
import StockpileNameInput from "../input-stockpile-name/input-stockpile-name.component";
import InputOutputSwitcher from "../input-output-switcher/input-output-switcher.component";
import TablesContainer from "../tables-container/tables-container.component";
import { UserInfo, MatchContext } from "../../context";
import prepareFeaturesData from "../../utils/prepare-features-data";
import { SET_DATA, SET_TABLE_INPUTS } from "../../reduce/actionTypes";
import ModalWindow from "../HOC/modal-window/modal-window.component";
import MatchingLoaderModal from "../matching-loader-modal/matching-loader-modal.component";
import useTrackEvent from "../../utils/hooks/use-track-app-event";
import {
  GET_LATEST_STOCKPILES,
  GET_RELINE,
} from "../../queries/stockpiles.gql";
import { DateTime } from "luxon";
import { RelineDates } from "../../queries/interfaces";

const Match = (): JSX.Element => {
  const { state, dispatch } = useContext(MatchContext);
  const trackEvent = useTrackEvent();
  const userInfo = useContext(UserInfo);
  const {
    loading,
    data: getLatestStockpiles,
    refetch: refetchLatestStockpiles,
  } = useQuery(GET_LATEST_STOCKPILES, {
    fetchPolicy: "no-cache",
  });

  const {
    loading: loadingReline,
    data: getReline,
    refetch: refetchReline,
  } = useQuery(GET_RELINE);

  const [dateValidationError, setDateValidationError] =
    useState<boolean>(false);

  const [relineValidation, setRelineValidation] = useState<boolean>(true);

  function findMostRecentDate(relineDates: RelineDates): DateTime | null {
    const dates: DateTime[] = [];

    for (const key in relineDates) {
      if (relineDates.hasOwnProperty(key) && relineDates[key]) {
        dates.push(DateTime.fromISO(relineDates[key] as string));
      }
    }

    if (dates.length === 0) {
      return null;
    }

    return DateTime.max(...dates);
  }

  function checkIfExecutedForCurrentDayAndTimePassed(date: string): boolean {
    const currentDateTime = DateTime.utc();
    const inputDateTime = DateTime.fromISO(date, { zone: "utc" });

    return (
      inputDateTime.hasSame(currentDateTime, "day") &&
      inputDateTime <= currentDateTime
    );
  }

  function isLessThan200DaysFromToday(dateTime: DateTime): boolean {
    const currentUtc = DateTime.utc();
    const differenceInDays = currentUtc.diff(dateTime, "days").days;

    return differenceInDays < 200;
  }

  function isDateOlderThan30Days(dateString: string) {
    const currentDate = DateTime.now();
    const inputDate = DateTime.fromFormat(dateString, "yyyy-MM-dd");
    const thirtyDaysAgo = currentDate.minus({ days: 30 });

    return inputDate < thirtyDaysAgo;
  }

  const [sendStockpilesData] = useMutation(SEND_STOCKPILES_TABLE_DATA, {
    onError: (err) => {
      dispatch({
        type: SET_DATA,
        payload: { userInputError: err },
      });
    },
    onCompleted: (data) => {
      logger.info("Request id: ", data.saveMatchingInputRequest?.requestId);
      dispatch({
        type: SET_DATA,
        payload: {
          requestId: data.saveMatchingInputRequest?.requestId,
        },
      });
      // "ca8e9aa0-cba4-4ef8-80e2-0f869dacd6d7"
    },
  });

  const handleStartChange = (e: any) => {
    dispatch({
      type: SET_DATA,
      payload: { startDate: e.target.value },
    });
  };

  const handleEndChange = (e: any) => {
    dispatch({
      type: SET_DATA,
      payload: { endDate: e.target.value },
    });
  };

  const handleSearchClick = () => {
    const featuresData = prepareFeaturesData(
      STOCKPILES_TABLE_FEATURES,
      state.tableInputs,
      state.tableWeights
    );

    trackEvent(APP_INSIGHTS_EVENTS.CLICK_SEARCH_STOCKPILE, {
      stockpileName: state.stockpileName,
      startDate: state.startDate,
      endDate: state.endDate,
      featuresData,
    });

    sendStockpilesData({
      variables: {
        stockpileName: state.stockpileName,
        startDate: state.startDate,
        endDate: state.endDate,
        userInfo: userInfo,
        featuresData,
      },
    });

    dispatch({
      type: SET_DATA,
      payload: { graphSensor: EMPTY_GRAPHS_VALUES },
    });

    dispatch({
      type: SET_DATA,
      payload: {
        showStockpileData: true,
        tableInputsPrevious: state.tableInputs,
        tableWeightsPrevious: state.tableWeights,
        startDatePrevious: state.startDate,
        endDatePrevious: state.endDate,
        forecastedStockpile: "",
      },
    });

    refetchLatestStockpiles().then(() =>
      setDateValidationError(
        isDateOlderThan30Days(
          getLatestStockpiles?.getLatestStockpiles[0].max_Pi_Date
        )
      )
    );

    refetchReline().then(() => {
      setRelineValidation(
        isLessThan200DaysFromToday(findMostRecentDate(getReline?.getReline[0]))
      );
    });
  };

  const handleClearTablesClick = () => {
    logger.info("clear tables");

    dispatch({
      type: SET_DATA,
      payload: { showStockpileData: false, stockpiles: null },
    });

    handleSwitch(RADIO_SWITCHER_VALUES.INPUT);
  };

  const handleChangeStockpileName = (e: any) => {
    dispatch({
      type: SET_DATA,
      payload: { stockpileName: e.target.value },
    });
  };

  const handleChangeTableInputs = (e: any) => {
    const name: string = e.target.id.toUpperCase();
    const value = Number(e.target.value);
    if (Math.abs(value) <= MAX_INPUT_VALUE) {
      dispatch({
        type: SET_TABLE_INPUTS,
        payload: { ...state.tableInputs, [name]: value },
      });
      handleValidateField("input", name, value);
    }
  };

  const handleChangeTableWeights = (e: any) => {
    const name: string = e.target.id.toUpperCase();
    const value = Number(e.target.value);
    if (Math.abs(value) <= MAX_INPUT_VALUE) {
      dispatch({
        type: SET_DATA,
        payload: { tableWeights: { ...state.tableWeights, [name]: value } },
      });
      handleValidateField("weight", name, value);
    }
  };

  const handleLoadMatchingRequests = (e: any) => {
    dispatch({
      type: SET_DATA,
      payload: { showRequestsModal: true },
    });
  };

  const handleSwitch = (value: string) => {
    dispatch({
      type: SET_DATA,
      payload: { ioSwitcherValue: value },
    });
  };

  const handleValidateField = (type: string, name: string, value: number) => {
    const key = `${name}-${type}`;

    if (type === "input") {
      if (value < 0 || value > 100) {
        dispatch({
          type: SET_DATA,
          payload: {
            inputErrors: {
              ...state.inputErrors,
              [key]: "Must be in range 0 - 100",
            },
          },
        });
      } else if (state.inputErrors[key]) {
        dispatch({
          type: SET_DATA,
          payload: {
            inputErrors: {
              ...state.inputErrors,
              [key]: null,
            },
          },
        });
      }
    }

    if (type === "weight") {
      if (value < 0 || value > 100) {
        dispatch({
          type: SET_DATA,
          payload: {
            inputErrors: {
              ...state.inputErrors,
              [key]: "Must be in range 0 - 100",
            },
          },
        });
      } else if (state.inputErrors[key]) {
        dispatch({
          type: SET_DATA,
          payload: {
            inputErrors: {
              ...state.inputErrors,
              [key]: null,
            },
          },
        });
      }
    }
  };

  const compareRecentInputsWithPrevious = () => {
    if (
      !Object.values(state.tableInputsPrevious).length &&
      !Object.values(state.tableWeightsPrevious).length
    ) {
      return false;
    }

    if (
      _.isEqual(state.tableInputsPrevious, state.tableInputs) &&
      _.isEqual(state.tableWeightsPrevious, state.tableWeights) &&
      state.startDatePrevious === state.startDate &&
      state.endDatePrevious === state.endDate &&
      state.showStockpileData
    ) {
      return true;
    }

    return false;
  };

  // check if all features with positive weight have inputs
  const checkFilledInputs = () => {
    const filled: any = [];

    Object.keys(STOCKPILES_TABLE_FEATURES).forEach((item) => {
      if (
        state.tableWeights[item] !== 0 &&
        state.tableInputs[item] != undefined
      )
        filled.push(true);
    });

    return (
      filled.length ===
      Object.values(state.tableWeights).filter((item) => item !== 0).length
    );
  };

  const checkReadyForSearch = () => {
    if (!state.startDate || !state.endDate) {
      logger.warn("[search] No date for search");
      return dispatch({
        type: SET_DATA,
        payload: { readyForSearch: false },
      });
    }

    if (!checkFilledInputs()) {
      logger.warn("[search] Not filled inputs");
      return dispatch({
        type: SET_DATA,
        payload: { readyForSearch: false },
      });
    }

    if (
      Object.values(state.inputErrors).filter((item) => item !== null).length
    ) {
      logger.warn("[search] Input errors");
      return dispatch({
        type: SET_DATA,
        payload: { readyForSearch: false },
      });
    }

    if (compareRecentInputsWithPrevious()) {
      logger.warn("[search] Identical input to previous");
      return dispatch({
        type: SET_DATA,
        payload: { readyForSearch: false },
      });
    }

    return dispatch({
      type: SET_DATA,
      payload: { readyForSearch: true },
    });
  };

  const handleClickHide = () => {
    trackEvent(APP_INSIGHTS_EVENTS.CHANGE_MATCHING_BODY, {
      value: !state.hideMatchingTable ? "hide" : "show",
    });
    dispatch({
      type: SET_DATA,
      payload: { hideMatchingTable: !state.hideMatchingTable },
    });
  };

  useEffect(() => {
    checkReadyForSearch();
  }, [
    state.inputErrors,
    state.tableInputs,
    state.tableWeights,
    state.tableInputsPrevious,
    state.tableWeightsPrevious,
    state.startDate,
    state.endDate,
    state.startDatePrevious,
    state.endDatePrevious,
    state.showStockpileData,
  ]);

  return (
    <div className="cards matching">
      <div className="custom-card card-common root">
        <ModalWindow show={state.showRequestsModal}>
          <MatchingLoaderModal />
        </ModalWindow>
        <div className={state.hideMatchingTable ? "hidden" : "header-content"}>
          <Grid container>
            <Grid item container xs={7} alignContent="flex-end">
              <Grid item>
                <div className="mr-2r">
                  <StockpileNameInput
                    handleChange={handleChangeStockpileName}
                  />
                </div>
              </Grid>
              <Grid item>
                <div className="mr-2r">
                  <TimeRangePicker
                    handleStartChange={handleStartChange}
                    handleEndChange={handleEndChange}
                  />
                </div>
              </Grid>
              <Grid item className="bottom-align">
                <div className="mr-2r">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchClick}
                    disabled={!state.readyForSearch}
                  >
                    Search
                  </Button>
                </div>
              </Grid>
              {state.showStockpileData && (
                <Grid item className="bottom-align">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClearTablesClick}
                  >
                    Clear Results
                  </Button>
                </Grid>
              )}
            </Grid>
            <Grid
              item
              container
              xs={5}
              justifyContent="flex-end"
              alignContent="flex-end"
            >
              <Grid item className="bottom-align">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleLoadMatchingRequests}
                >
                  Load
                </Button>
              </Grid>
              <Grid item>
                <div className="ml-2r">
                  <InputOutputSwitcher handleSwitch={handleSwitch} />
                </div>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div>
          {dateValidationError ? (
            <p
              style={{
                color: "#ed6c02",
                paddingLeft: "calc(var(--base-padding) * 2)",
              }}
            >
              Incomplete Search Results. The search may not return all matchers
              as the latest stockpile updated was received on{" "}
              {getLatestStockpiles?.getLatestStockpiles[0].max_Pi_Date}
            </p>
          ) : (
            <p style={{ paddingLeft: "calc(var(--base-padding) * 2)" }}></p>
          )}
          {!relineValidation ? (
            <p
              style={{
                color: "#ed6c02",
                paddingLeft: "calc(var(--base-padding) * 2)",
              }}
            >
              Incomplete Search Results. The search may not return all matchers
              as the latest reline updated was received on{" "}
              {!loadingReline &&
                findMostRecentDate(getReline?.getReline[0]).toFormat(
                  "yyyy-MM-dd"
                )}
            </p>
          ) : (
            <p style={{ paddingLeft: "calc(var(--base-padding) * 2)" }}></p>
          )}
        </div>
        <div className="collapse">
          <i onClick={handleClickHide} role="button" tabIndex={0}>
            <KeyboardTab className={state.hideMatchingTable && "clicked"} />
          </i>
        </div>
        <TablesContainer
          handleChangeTableInputs={handleChangeTableInputs}
          handleChangeTableWeights={handleChangeTableWeights}
        />
      </div>
    </div>
  );
};

export default Match;
